import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'foundations-components/transitional/components/Modal';
// @ts-expect-error untyped dependency
import IFrame from 'ui-addon-iframeable/host/IFrame';
import ScopesAuthorization from 'developer-experience-shared-components/scopes/v3/components/ScopesAuthorization';
import { Button } from 'foundations-components/transitional/components/Button';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Tile, TileSection } from 'foundations-components/transitional/components/Tile';
import { View } from 'foundations-components/components/View';
import { Wizard, WizardHeaderWithOverview, WizardStep } from 'foundations-components/transitional/components/Wizard';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { Flex } from 'foundations-components/components/Flex';
import { Image } from 'foundations-components/transitional/components/Image';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Text } from 'foundations-components/components/Text';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { getFullUrl } from 'hubspot-url-utils';
import { EllipsesIcon } from 'foundations-assets/icons/components/EllipsesIcon';
import { SprocketIcon } from 'foundations-assets/icons/components/SprocketIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export function InstallWizard({
  appId,
  isPreview,
  isUIE,
  iconUrl,
  name,
  oauthQueryParams,
  offeringId,
  onClose,
  onTrackInteraction = () => null,
  portalId
}) {
  const incomingOAuthQueryParams = useMemo(() => new URLSearchParams(oauthQueryParams), [oauthQueryParams]);
  const [stepIndex, setStepIndex] = useState(0);
  const [redirectUri, setRedirectUri] = useState('');
  const [popUpWindow, setPopUpWindow] = useState(null);
  useEffect(() => {
    const handleInstallSuccess = event => {
      // check that message originated from the same origin
      if (event.origin !== getFullUrl('app')) return;
      if (event.data && event.data.type === 'INSTALL_SUCCESS') {
        if (popUpWindow) popUpWindow.close();
        setStepIndex(2);
        onTrackInteraction('complete third party sign in');
      }
    };
    if (stepIndex === 1 && popUpWindow) {
      window.addEventListener('message', handleInstallSuccess);
    }
    if (stepIndex === 2) {
      window.removeEventListener('message', handleInstallSuccess);
    }
  }, [onTrackInteraction, popUpWindow, stepIndex]);
  const renderedHeaderIcons = useMemo(() => /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    justifySelf: "center",
    marginBlockEnd: "400",
    children: [/*#__PURE__*/_jsx(Image, {
      src: iconUrl,
      blockSize: "64px",
      objectFit: "contain",
      responsive: false
    }), /*#__PURE__*/_jsx(IconHolder, {
      icon: EllipsesIcon,
      size: "1000",
      paddingBlock: "0",
      color: "icon-secondary-default"
    }), /*#__PURE__*/_jsx(IconHolder, {
      icon: SprocketIcon,
      size: "1000",
      padding: "0",
      color: "icon-secondary-default"
    })]
  }), [iconUrl]);
  const handlePopUpClick = useCallback(url => {
    if (popUpWindow && !popUpWindow.closed) {
      popUpWindow.focus();
      onTrackInteraction('focus third party sign in pop up');
    } else {
      const windowObjReference = window.open(url, '_target', 'popup=true,width=600,height=800,top=200,left=200');
      setPopUpWindow(windowObjReference);
      onTrackInteraction('open third party sign in pop up');
    }
  }, [onTrackInteraction, popUpWindow, setPopUpWindow]);
  const renderedPopUpCTA = useMemo(() => {
    // For the Preview, the code query param returned by HubSpot OAuth in Step 1
    // e.g. https://exampleredirectui.com?code=1234-5678
    // is mocked with the placeholder of HUBSPOT_CODE_VALUE
    const previewRedirectUri = `${incomingOAuthQueryParams.get('redirect_uri')}?code=HUBSPOT_CODE_VALUE`;
    const popUpUrl = `${isPreview ? previewRedirectUri : redirectUri}&returnUrl=${getFullUrl('app')}/ecosystem/${portalId}/marketplace/apps/${offeringId}/install-success`;
    if (popUpWindow) {
      return /*#__PURE__*/_jsxs(Tile, {
        compact: true,
        padding: "800",
        marginBlockStart: "600",
        children: [/*#__PURE__*/_jsx(TileSection, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-ui-apps-core.installFlow.step2.loginPage",
            options: {
              name
            }
          })
        }), /*#__PURE__*/_jsx(TileSection, {
          children: /*#__PURE__*/_jsx(Button, {
            use: "link",
            onClick: () => handlePopUpClick(popUpUrl),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-ui-apps-core.installFlow.step2.openLink"
            })
          })
        })]
      });
    } else {
      return /*#__PURE__*/_jsx(Button, {
        use: "primary",
        marginBlockStart: "600",
        onClick: () => handlePopUpClick(popUpUrl),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-apps-core.installFlow.step2.signIn"
        })
      });
    }
  }, [handlePopUpClick, incomingOAuthQueryParams, isPreview, name, offeringId, portalId, popUpWindow, redirectUri]);
  const renderedOAuthPreview = useMemo(() => {
    var _incomingOAuthQueryPa;
    const scopesStrings = ((_incomingOAuthQueryPa = incomingOAuthQueryParams.get('scope')) === null || _incomingOAuthQueryPa === void 0 ? void 0 : _incomingOAuthQueryPa.split(' ')) || [];
    // @ts-expect-error Partial ScopeGroups for preview
    const scopes = scopesStrings.map(scopeName => ({
      name: scopeName
    }));
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [renderedHeaderIcons, /*#__PURE__*/_jsx(Text, {
        as: "h2",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-apps-core.installFlow.step1.header",
          options: {
            name
          }
        })
      }), /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        alignItems: "start",
        justifySelf: "center",
        children: /*#__PURE__*/_jsxs(Tile, {
          marginInline: "800",
          marginBlock: "600",
          children: [/*#__PURE__*/_jsxs(TileSection, {
            children: [/*#__PURE__*/_jsx(Text, {
              as: "h6",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "marketplace-ui-apps-core.installFlow.step1.subheader"
              })
            }), /*#__PURE__*/_jsx(Flex, {
              flexDirection: "column",
              alignItems: "start",
              justifyContent: "start",
              children: /*#__PURE__*/_jsx(ScopesAuthorization, {
                scopes: scopes
              })
            })]
          }), /*#__PURE__*/_jsx(Tooltip, {
            title: unescapedText('marketplace-ui-apps-core.installFlow.step1.connectTooltip'),
            children: /*#__PURE__*/_jsx(Button, {
              disabled: true,
              marginBlockEnd: "600",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "marketplace-ui-apps-core.installFlow.step1.connectApp"
              })
            })
          })]
        })
      })]
    });
  }, [incomingOAuthQueryParams, name, renderedHeaderIcons]);
  const renderedInstallSuccessButtons = useMemo(() => {
    return /*#__PURE__*/_jsxs(ButtonWrapper, {
      marginBlockStart: "400",
      justify: "center",
      children: [/*#__PURE__*/_jsx(Button, {
        use: "primary",
        href: `/integrations-settings/${portalId}/installed/application/${appId}/featureDiscovery`,
        onClick: () => onTrackInteraction('click feature discovery link'),
        marginBlockStart: "400",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-apps-core.installFlow.step3.featureDiscovery"
        })
      }), isUIE ? /*#__PURE__*/_jsx(Button, {
        href: `/integrations-settings/${portalId}/installed/application/${appId}/app-cards`,
        onClick: () => onTrackInteraction('click app cards link'),
        marginBlockStart: "400",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-apps-core.installFlow.step3.uie"
        })
      }) : /*#__PURE__*/_jsx(Button, {
        onClick: onClose,
        marginBlockStart: "400",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-apps-core.installFlow.step3.close"
        })
      })]
    });
  }, [appId, isUIE, onClose, onTrackInteraction, portalId]);
  return /*#__PURE__*/_jsxs(Modal, {
    use: "conversational",
    width: 800,
    size: "tall",
    centered: true,
    children: [/*#__PURE__*/_jsx(DialogCloseButton, {
      onClick: onClose
    }), /*#__PURE__*/_jsx(View, {
      marginBlockStart: "800",
      children: /*#__PURE__*/_jsxs(Wizard, {
        headerComponent: WizardHeaderWithOverview,
        stepIndex: stepIndex,
        onStepIndexChange: e => {
          const index = +e.target.value;
          setStepIndex(index);
        },
        onReject: () => null,
        onConfirm: () => null,
        footerComponent: isPreview ? undefined : () => null,
        children: [/*#__PURE__*/_jsx(WizardStep, {
          name: unescapedText('marketplace-ui-apps-core.installFlow.step1.name'),
          padding: "0",
          children: isPreview ? renderedOAuthPreview : /*#__PURE__*/_jsx(IFrame, {
            src: `${getFullUrl('app')}/oauth/${portalId}/embedded/authorize${oauthQueryParams}`,
            embeddedPassthruProps: {
              allowParentRedirect: true
            },
            onReady: () => {},
            onClose: () => {},
            onMessage: ({
              payload
            }) => {
              if (payload.type === 'OAUTH_SUCCESS') {
                setStepIndex(1);
                setRedirectUri(payload.redirectUri);
                onTrackInteraction('complete hubspot oauth');
              }
            },
            width: "800",
            height: "700",
            use: "default"
          })
        }), /*#__PURE__*/_jsxs(WizardStep, {
          name: unescapedText('marketplace-ui-apps-core.installFlow.step2.name', {
            name
          }),
          children: [renderedHeaderIcons, /*#__PURE__*/_jsx(Text, {
            as: "h1",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-ui-apps-core.installFlow.step2.header",
              options: {
                name
              }
            })
          }), /*#__PURE__*/_jsxs(Flex, {
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            marginBlockStart: "300",
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-ui-apps-core.installFlow.step2.subheader",
              options: {
                name
              }
            }), renderedPopUpCTA]
          })]
        }), /*#__PURE__*/_jsxs(WizardStep, {
          name: unescapedText('marketplace-ui-apps-core.installFlow.step3.name'),
          children: [renderedHeaderIcons, /*#__PURE__*/_jsx(Text, {
            as: "h1",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-ui-apps-core.installFlow.step3.header",
              options: {
                name
              }
            })
          }), /*#__PURE__*/_jsxs(Flex, {
            flexDirection: "column",
            alignItems: "center",
            marginBlockStart: "300",
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-ui-apps-core.installFlow.step3.subheader"
            }), renderedInstallSuccessButtons]
          })]
        })]
      })
    })]
  });
}